import { utils } from "@hh/global.js";
const { updateCart, reRenderShopifySections } = utils;

/**
 * Bridges the LoyaltyLion SDK to the AJAX mini cart, both ways: SDK cart events
 * re-render the [data-rerender] nodes, theme cart changes call ui.refresh().
 * The reference impl reloads the page instead; we have no cart page to reload.
 */

// The SDK is injected by an app embed, which may resolve after this module.
const SDK_POLL_INTERVAL = 200;
const SDK_POLL_TIMEOUT = 15000;

// reRenderShopifySections fires CART:TEMPLATE_UPDATE once per section key.
const REFRESH_DEBOUNCE = 100;

const getSdk = () => {
  const sdk = window.loyaltylion || window.lion;
  return sdk && typeof sdk.on === "function" ? sdk : null;
};

class LoyaltyLionCartRewards extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.syncing = false;
    this.refreshTimer = null;

    this.onTemplateUpdate = this.onTemplateUpdate.bind(this);
    document.addEventListener("CART:TEMPLATE_UPDATE", this.onTemplateUpdate);

    this.whenSdkReady().then((sdk) => sdk && this.bind(sdk));
  }

  disconnectedCallback() {
    document.removeEventListener("CART:TEMPLATE_UPDATE", this.onTemplateUpdate);
    clearTimeout(this.refreshTimer);
  }

  whenSdkReady() {
    return new Promise((resolve) => {
      const existing = getSdk();
      if (existing) return resolve(existing);

      const startedAt = Date.now();
      const poll = setInterval(() => {
        const sdk = getSdk();
        if (sdk) {
          clearInterval(poll);
          resolve(sdk);
        } else if (Date.now() - startedAt > SDK_POLL_TIMEOUT) {
          // App embed is off for this store; leave the widget as inert markup.
          clearInterval(poll);
          resolve(null);
        }
      }, SDK_POLL_INTERVAL);
    });
  }

  bind(sdk) {
    // cart.changed is the documented event, the rest are older-SDK fallbacks.
    // syncCart() is re-entrancy guarded, so overlapping events are harmless.
    ["cart.changed", "cart.addedItem", "cart.removedItems"].forEach((event) => {
      try {
        sdk.on(event, () => this.syncCart());
      } catch (error) {
        // Unknown event names throw on some SDK versions.
      }
    });
  }

  /**
   * SDK -> theme. A no-op /cart/update.js POST returns section HTML in exactly
   * the shape reRenderShopifySections wants, on the same path as the rest of
   * the cart.
   */
  async syncCart() {
    if (this.syncing) return;
    this.syncing = true;
    this.classList.add("loyaltylion-rewards--working");

    try {
      const response = await updateCart({});
      if (response?.sections) {
        reRenderShopifySections(response, "[data-rerender]");
      }
      // Re-rendering does not update the drawer's empty state. Call the method
      // directly rather than dispatching `productAdded`, which hh-broadcast
      // elements elsewhere also listen for.
      document.querySelector("hh-mini-cart")?.checkCartStatus?.();
    } catch (error) {
      console.error("LoyaltyLion: failed to re-render cart", error);
    } finally {
      this.classList.remove("loyaltylion-rewards--working");
      this.syncing = false;
    }
  }

  // theme -> SDK. The SDK does not observe its own components for changes.
  onTemplateUpdate() {
    clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      window.loyaltylion?.ui?.refresh?.();
    }, REFRESH_DEBOUNCE);
  }
}

if (!customElements.get("loyaltylion-cart-rewards")) {
  customElements.define("loyaltylion-cart-rewards", LoyaltyLionCartRewards);
}
